/**
 * Absences (blessés/suspendus) servies par /data/injuries.json (refresh QUOTIDIEN
 * serveur — voir server/modelData.mjs). Chargées une fois puis mises en cache ; un
 * échec réseau laisse l'app muette, jamais d'écran cassé (règle 8). Source du badge
 * « absent » (fiche pays + mini-terrain) et de la pénalité d'absents du modèle de
 * projection (EXPÉRIMENTAL, plafonné — voir src/ui/projection.ts).
 */
export interface Absence {
  readonly playerId: number;
  readonly name: string;
  readonly type: string; // "Missing Fixture" (absent) | "Questionable" (incertain)
  readonly reason: string; // texte libre (blessure OU suspension)
  readonly suspended: boolean;
}

interface InjuriesDoc {
  readonly injuries?: Record<string, Absence[]>;
}

let byTeam: Record<string, Absence[]> | null = null;
let byId: Map<number, Absence> | null = null;
let pending: Promise<void> | null = null;

/** Charge /data/injuries.json une seule fois (re-tente tant que pas chargé). */
export function ensureInjuries(): Promise<void> {
  if (byTeam) return Promise.resolve(); // déjà chargé (un {} vide est un état valide)
  if (pending) return pending;
  pending = fetch("data/injuries.json")
    .then((r) => (r.ok ? (r.json() as Promise<InjuriesDoc>) : null))
    .then((doc) => {
      if (doc?.injuries) {
        byTeam = doc.injuries;
        byId = new Map();
        for (const arr of Object.values(byTeam)) {
          for (const a of arr) if (typeof a.playerId === "number") byId.set(a.playerId, a);
        }
      }
    })
    .catch(() => {
      /* règle 8 : pas d'absences → aucun badge, aucune pénalité */
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** Précharge pour que le 1er badge / calcul soit instantané. */
export function preloadInjuries(): void {
  void ensureInjuries();
}

/** L'absence d'un joueur par son id API-Football (lecture synchrone du cache), ou null. */
export function isAbsent(playerId: number): Absence | null {
  return byId?.get(playerId) ?? null;
}

/** Les ids absents d'une sélection (code FIFA). Set vide si rien / non chargé. */
export function absentIdsOf(teamCode: string): Set<number> {
  return new Set((byTeam?.[teamCode] ?? []).map((a) => a.playerId));
}

/**
 * Pénalité d'absents PAR code équipe pour le modèle (EXPÉRIMENTAL) : somme de poids
 * — « Missing Fixture » = poids plein (1), « Questionable » = demi-poids (0,5). Le
 * PLAFOND est appliqué côté projection (applyAbsenceFactor). null tant que non chargé.
 */
export function absencePenalties(): Record<string, number> | null {
  if (!byTeam) return null;
  const out: Record<string, number> = {};
  for (const [code, arr] of Object.entries(byTeam)) {
    let w = 0;
    for (const a of arr) w += a.type === "Questionable" ? 0.5 : 1;
    if (w > 0) out[code] = w;
  }
  return out;
}
